import { useDeepCompareMemo } from "@react-hookz/web";
import { useAtomValue } from "jotai";

import {
  TargetingHomePageTakeOverType,
  TargetingPaidType,
} from "~/components/advertisement/ad-slots/types";
import { currentTakeoverAdSectionsAtom } from "~/components/advertisement/takeover-ad-slot/atoms";
import {
  isStyleMainSectionBySectionPath,
  isStyleSectionContent,
} from "~/components/section/section-style/helpers";
import { appAtom } from "~/lib/app/atoms";
import { useIpGeoLocation } from "~/lib/hooks";
import { rosettaAtom } from "~/lib/rosetta";
import { PageType } from "~/lib/router/enums";
import { useCurrentPageType } from "~/lib/router/hooks";

import type { CurrentPageAdSlotInfoType } from "./types";

export const useTakeoverAdSlot = () => {
  const { advertisementConfig } = useAtomValue(appAtom);
  const currentPageType = useCurrentPageType();
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const ipGeoLocation = useIpGeoLocation();
  const currentSectionPath = useAtomValue(currentTakeoverAdSectionsAtom);

  const currentPageTakeoverAdConfig = isStyleSectionContent(currentSectionPath)
    ? advertisementConfig.takeoverAd.style
    : null;

  const currentPageTakeoverAdSlotInfo = useDeepCompareMemo(() => {
    if (!currentPageTakeoverAdConfig) return;

    const baseAdSlotValue: CurrentPageAdSlotInfoType = {
      adSlotValue: {
        adUnit: "hpto1",
        autoRefreshOptions: {
          intervalRefreshOptions: {
            isDisable: true,
          },
        },
        targeting: {
          hpto: TargetingHomePageTakeOverType.Yes,
          paid: TargetingPaidType.Free,
        },
        zone: "style/home",
      },
      enable: true,
    };

    switch (currentPageType) {
      // TODO: handle home page takeover ad
      case PageType.Article:
        if (!currentPageTakeoverAdConfig.enableInPageTypes.article.enable) return;
        return baseAdSlotValue;

      case PageType.Section: {
        const isMainSection = isStyleMainSectionBySectionPath(currentSectionPath);
        const enable = isMainSection
          ? currentPageTakeoverAdConfig.enableInPageTypes.mainSection.enable
          : currentPageTakeoverAdConfig.enableInPageTypes.subSection.enable;
        if (!enable) return;

        return baseAdSlotValue;
      }
      default:
        return;
    }
  }, [currentSectionPath, currentPageTakeoverAdConfig, currentPageType]);

  const isEnableAdSlot = (config = currentPageTakeoverAdConfig) => {
    if (!config || !currentPageTakeoverAdSlotInfo) return false;

    if (config.ipAddress.length > 0) {
      const userIps = ipGeoLocation?.result?.ip.split(",").map(ip => ip.trim()) ?? [];
      if (!userIps.every(element => config.ipAddress.includes(element))) return false;
    }

    if (config.country.length > 0) {
      const userCountry = ipGeoLocation?.result?.country ?? "";

      if (!userCountry) return false;
      if (!config.country.includes(userCountry)) return false;
    }

    if (config.targetSubscribersOnly || config.targetNonSubscribersOnly) {
      const isScmpSubscriber = asyncRosettaState?.result?.user?.isSCMPSubscriber ?? false;
      if (config.targetSubscribersOnly && !isScmpSubscriber) return false;
      if (config.targetNonSubscribersOnly && isScmpSubscriber) return false;
    }

    // Priority: alwaysEnable > enableTakeoverByDateTime > is enabled for current content
    if (config.alwaysEnable) return true;

    if (config.enableTakeoverByDateTime && currentPageTakeoverAdSlotInfo.enable) {
      const now = new Date();
      const startTime = new Date(config.startDateTime);
      const endTime = new Date(config.endDateTime);
      return now >= startTime && now <= endTime;
    }

    return currentPageTakeoverAdSlotInfo.enable;
  };

  return {
    isEnableTakeoverAdSlot: isEnableAdSlot(),
    takeoverAdSlotValue: currentPageTakeoverAdSlotInfo?.adSlotValue,
  };
};
